/**
 * Common types and utilities for file viewers.
 * Used by CanvasFileViewer, RefactorFileViewerSlide, and other file viewing components.
 */

/** Maximum file size (in bytes) that can be rendered inline */
export const MAX_RENDERABLE_SIZE = 1 * 1024 * 1024 // 1MB

/**
 * Standardized file item for file viewers.
 * All file sources (S3, proto canvas data, diffs) should be normalized to this format.
 */
export interface FileViewerItem {
  /** Full filename with extension, e.g., "report.md", "chart.png" */
  filename: string

  /** The actual content - text or base64-encoded binary */
  content: string

  /** How the content is encoded */
  contentEncoding: 'text' | 'base64'

  /** MIME type, e.g., "text/markdown", "image/png" */
  mimeType: string

  /** Original size in bytes (optional) */
  size?: number

  /** True if file exceeds MAX_RENDERABLE_SIZE - show download only */
  isTooLarge?: boolean
}

/**
 * MIME type mapping for common file extensions.
 */
const MIME_TYPE_MAP: Record<string, string> = {
  // Images
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  bmp: 'image/bmp',

  // Text
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',

  // Data
  json: 'application/json',
  xml: 'application/xml',
  yaml: 'text/yaml',
  yml: 'text/yaml',

  // Code
  js: 'text/javascript',
  jsx: 'text/javascript',
  ts: 'text/typescript',
  tsx: 'text/typescript',
  py: 'text/x-python',
  java: 'text/x-java',
  kt: 'text/x-kotlin',
  go: 'text/x-go',
  rs: 'text/x-rust',
  c: 'text/x-c',
  cpp: 'text/x-c++',
  h: 'text/x-c',
  hpp: 'text/x-c++',
  cs: 'text/x-csharp',
  rb: 'text/x-ruby',
  php: 'text/x-php',
  swift: 'text/x-swift',
  sh: 'text/x-shellscript',
  bash: 'text/x-shellscript',
  zsh: 'text/x-shellscript',
  sql: 'text/x-sql',

  // Web
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  scss: 'text/x-scss',
  sass: 'text/x-sass',
  less: 'text/x-less',

  // Config
  toml: 'text/x-toml',
  ini: 'text/x-ini',
  cfg: 'text/x-ini',
  conf: 'text/x-ini',
  env: 'text/plain',
  properties: 'text/x-java-properties',

  // Binary
  pdf: 'application/pdf',
  zip: 'application/zip',
  gz: 'application/gzip',
  tar: 'application/x-tar',
  rar: 'application/x-rar-compressed',
  '7z': 'application/x-7z-compressed',

  // Documents
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
}

/**
 * Get MIME type from filename extension.
 * @param filename - The filename to analyze
 * @returns MIME type string, defaults to 'application/octet-stream' for unknown types
 */
export function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase()
  return MIME_TYPE_MAP[ext || ''] || 'application/octet-stream'
}

/**
 * Get file extension from MIME type.
 * @param mimeType - The MIME type
 * @returns File extension without dot (e.g., 'png', 'md'), or empty string if unknown
 */
export function getExtensionFromMimeType(mimeType: string): string {
  // Reverse lookup in MIME_TYPE_MAP
  for (const [ext, mime] of Object.entries(MIME_TYPE_MAP)) {
    if (mime === mimeType) {
      return ext
    }
  }
  return ''
}

/**
 * Ensure filename has the correct extension based on MIME type.
 * If filename already has an extension, returns as-is.
 * If not, appends the extension derived from mimeType.
 *
 * @param filename - The original filename
 * @param mimeType - The MIME type to use for extension
 * @returns Filename with proper extension
 */
export function ensureFileExtension(filename: string, mimeType: string): string {
  // Check if filename already has an extension
  const lastDot = filename.lastIndexOf('.')
  const hasExtension = lastDot > 0 && lastDot > filename.lastIndexOf('/')

  if (hasExtension) {
    return filename
  }

  // Append extension from mimeType
  const ext = getExtensionFromMimeType(mimeType)
  return ext ? `${filename}.${ext}` : filename
}

/**
 * Check if a MIME type represents a text-based file that can be rendered inline.
 * @param mimeType - The MIME type to check
 * @returns True if the file can be rendered as text
 */
export function isTextMimeType(mimeType: string): boolean {
  return (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml'
  )
}

/**
 * Check if a MIME type represents an image that can be rendered inline.
 * @param mimeType - The MIME type to check
 * @returns True if the file can be rendered as an image
 */
export function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

/**
 * Check if a MIME type represents markdown content.
 * @param mimeType - The MIME type to check
 * @returns True if the file should be rendered as markdown
 */
export function isMarkdownMimeType(mimeType: string): boolean {
  return mimeType === 'text/markdown'
}

/**
 * Check if a MIME type represents HTML content.
 * Used to render self-contained HTML/JS payloads inside a sandboxed iframe.
 * @param mimeType - The MIME type to check
 * @returns True if the file should be rendered as a live HTML page
 */
export function isHtmlMimeType(mimeType: string): boolean {
  return mimeType === 'text/html'
}

/**
 * Convert base64 string to Blob for download.
 * Handles both raw base64 and data URI formats.
 *
 * @param content - Base64 string (raw or data URI format)
 * @param mimeType - MIME type for the resulting Blob
 * @returns Blob containing the binary data
 */
export function base64ToBlob(content: string, mimeType: string): Blob {
  let base64Data: string
  let actualMimeType: string = mimeType

  if (content.startsWith('data:')) {
    // Data URI format: "data:image/jpeg;base64,/9j/4AAQ..."
    const [header, data] = content.split(',')
    base64Data = data || ''
    const mimeMatch = header.match(/data:(.*?);/)
    if (mimeMatch?.[1]) {
      actualMimeType = mimeMatch[1]
    }
  } else {
    // Raw base64
    base64Data = content
  }

  try {
    const binary = atob(base64Data)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return new Blob([bytes], { type: actualMimeType })
  } catch (error) {
    console.error('Failed to decode base64:', error)
    return new Blob([], { type: actualMimeType })
  }
}

/**
 * Create a data URI from base64 content for rendering in <img> tags.
 * Handles both raw base64 and existing data URI formats.
 *
 * @param content - Base64 string (raw or data URI format)
 * @param mimeType - MIME type for the data URI
 * @returns Data URI string suitable for img src
 */
export function createDataUri(content: string, mimeType: string): string {
  if (content.startsWith('data:')) {
    // Already a data URI
    return content
  }
  return `data:${mimeType};base64,${content}`
}

/**
 * Estimate the size of base64 content in bytes.
 * Base64 encoding increases size by ~33%.
 *
 * @param base64Content - The base64 encoded string
 * @returns Estimated original size in bytes
 */
export function estimateBase64Size(base64Content: string): number {
  // Remove data URI prefix if present
  const base64Only = base64Content.includes(',') ? base64Content.split(',')[1] || '' : base64Content

  // Base64 encodes 3 bytes as 4 characters
  // Account for padding (=)
  const padding = (base64Only.match(/=+$/) || [''])[0].length
  return Math.floor((base64Only.length * 3) / 4) - padding
}
